// Part of purchasePages, split by screen to keep files short; purchasePages.ts merges the parts.
export const en = {
  import: {
    title: 'Import bank statement',
    description:
      'Upload a CSV or OFX file from your bank. Transactions go to Bank transactions › For review.',
    back: 'Back to bank transactions',
    staffOnly: 'Only accounting staff can import statements',
    staffOnlyHint: 'Send the statement to your accountant.',
    loadFailed: 'Could not load bank accounts',
    noBanks: 'No bank accounts yet',
    noBanksHint: 'Add a Bank or Credit card account to the chart of accounts first.',
    steps: {
      upload: 'Account and file',
      map: 'Map columns',
      review: 'Review',
      done: 'Done',
    },
    stepsLabel: 'Import steps',
    fields: {
      date: 'Date',
      description: 'Description',
      payee: 'Payee',
      reference: 'Reference',
      amount: 'Amount',
      moneyIn: 'Money in',
      moneyOut: 'Money out',
      balance: 'Balance',
    },
    listSeparator: ', ',
    wrongFile: 'Choose a .csv or .ofx file. In Excel, use File › Save as › CSV UTF-8.',
    noRows: 'The file has a header row but no transactions.',
    tooManyRows: 'The file has {{rows}} rows; import at most {{max}} at a time.',
    bankAccount: 'Bank account',
    dropTitle: 'Drag and drop a statement file here, or browse',
    dropHint:
      'CSV (any bank: you match the columns once, then we remember them for this account) or OFX / QFX. Up to {{max}} transactions per file. Transactions already imported are skipped.',
    reading: 'Reading…',
    browse: 'Browse',
    fileLabel: 'Statement file',
    hintDescription: 'Narrative or details. Payee is used when blank.',
    hintReference: 'Cheque or transaction reference.',
    hintMoneyIn: 'Credits / deposits.',
    hintMoneyOut: 'Debits / withdrawals.',
    hintBalance: 'Running balance; the latest one becomes the bank balance.',
    mapMissing: 'Map a column to {{fields}}.',
    mapSplitMissing: 'Map the money in or money out column.',
    mapTwice: 'Each column can feed one field: {{columns}} is mapped twice.',
    mapIntroSaved_one:
      '{{n}} row into <b>{{bank}}</b>. We used the mapping saved for this account; check the rest. The mapping is remembered for this account.',
    mapIntroSaved_other:
      '{{n}} rows into <b>{{bank}}</b>. We used the mapping saved for this account; check the rest. The mapping is remembered for this account.',
    mapIntroAuto_one:
      '{{n}} row into <b>{{bank}}</b>. We matched what we recognised; check the rest. The mapping is remembered for this account.',
    mapIntroAuto_other:
      '{{n}} rows into <b>{{bank}}</b>. We matched what we recognised; check the rest. The mapping is remembered for this account.',
    dateFormat: 'Date format',
    amountsIn: 'Amounts are in',
    oneColumn: 'One column (positive and negative)',
    twoColumns: 'Two columns (money in, money out)',
    positiveAre: 'Positive amounts are',
    positiveIn: 'Money in (most bank accounts)',
    positiveOut: 'Money out (most credit cards)',
    colField: 'Outss Accounting field',
    colColumn: 'Column in your file',
    colFirstRow: 'First row',
    columnFor: 'Column for {{field}}',
    notMapped: 'Not mapped',
    back2: 'Back',
    checking: 'Checking transactions…',
    next: 'Next',
    imported_one: 'Imported {{n}} transaction into {{bank}}.',
    imported_other: 'Imported {{n}} transactions into {{bank}}.',
    autoAdded_one: '{{n}} was added automatically by rules.',
    autoAdded_other: '{{n}} were added automatically by rules.',
    ruleSuggested_one: '{{n}} has a rule suggestion.',
    ruleSuggested_other: '{{n}} have a rule suggestion.',
    duplicatesSkipped_one: '{{n}} duplicate was skipped.',
    duplicatesSkipped_other: '{{n}} duplicates were skipped.',
    errorsSkipped_one: '{{n}} row with errors was skipped.',
    errorsSkipped_other: '{{n}} rows with errors were skipped.',
    ready_one: '{{ok}} of {{n}} transaction is ready to import.',
    ready_other: '{{ok}} of {{n}} transactions are ready to import.',
    duplicatesWillSkip_one: '{{n}} is already in Outss Accounting and will be skipped.',
    duplicatesWillSkip_other: '{{n}} are already in Outss Accounting and will be skipped.',
    errorsWillSkip_one: '{{n}} row has errors and will be skipped.',
    errorsWillSkip_other: '{{n}} rows have errors and will be skipped.',
    statementBalance: 'Statement balance {{amount}} on {{date}}.',
    onlyProblems: 'Show only duplicates and errors',
    colRow: 'Row',
    colDate: 'Date',
    colDescription: 'Description',
    colAmount: 'Amount',
    colStatus: 'Status',
    colErrors: 'Errors',
    statusImported: 'Imported',
    statusReady: 'Ready',
    statusDuplicate: 'Duplicate',
    statusError: 'Error',
    transaction: 'Transaction',
    errorField: '{{field}}: ',
    another: 'Import another file',
    reviewTransactions: 'Review transactions',
    importing: 'Importing…',
    importN_one: 'Import {{n}} transaction',
    importN_other: 'Import {{n}} transactions',
  },
  reconcile: {
    title: 'Reconcile',
    description:
      'Compare an account with its bank statement, tick what cleared, and finish when the difference is zero.',
    loadFailed: 'Could not load bank accounts',
    noBanks: 'No bank accounts to reconcile',
    statusInProgress: 'In progress',
    statusReconciled: 'Reconciled',
    statusUndone: 'Undone',
    deleted: 'Reconciliation deleted. Nothing was changed in the books.',
    whichAccount: 'Which account do you want to reconcile?',
    account: 'Account',
    lastEnding: 'Last statement ending date {{date}}',
    neverReconciled: 'Never reconciled',
    savedForLater: 'A reconciliation to {{date}} (ending balance {{amount}}) was saved for later.',
    resumeReconciling: 'Resume reconciling',
    addInfo: 'Add the following information',
    beginningBalance: 'Beginning balance',
    beginningHint: 'From the last reconciliation.',
    endingBalanceRequired: 'Ending balance *',
    endingCardHint: 'The amount owed on the statement.',
    endingDateRequired: 'Ending date *',
    negativeCard: 'The ending balance is negative (the card is in credit)',
    negativeBank: 'The ending balance is negative (overdrawn)',
    starting: 'Starting…',
    start: 'Start reconciling',
    historyFor: 'History for {{name}}',
    noHistory: 'No reconciliations yet',
    colEndingDate: 'Statement ending date',
    colStatus: 'Status',
    colEndingBalance: 'Ending balance',
    colBeginningBalance: 'Beginning balance',
    colReconciledOn: 'Reconciled on',
    colAction: 'Action',
    resume: 'Resume',
    delete: 'Delete',
    viewReport: 'View report',
    undo: 'Undo',
    deleteTitle: 'Delete the reconciliation in progress?',
    deleteBody: 'The ticks saved for later are discarded. Nothing in the books changes.',
    undoneToast: 'Reconciliation undone. Its transactions are no longer reconciled.',
    undoTitle: 'Undo the reconciliation to {{date}}?',
    undoBody:
      'Every transaction it cleared goes back to uncleared, and can be changed or reversed again. The reconciliation stays in the history, marked undone. Nothing is posted.',
    reason: 'Reason',
    reasonHint: 'Recorded in the audit log.',
    undoing: 'Undoing…',
    undoReconciliation: 'Undo reconciliation',
    // Workspace
    loadReconciliationFailed: 'Could not load the reconciliation',
    leaveConfirm: 'Leave without saving your ticks? Use Save for later to keep them.',
    savedToast: 'Saved for later. Resume it from Reconcile.',
    finishedToast: '{{name}} is reconciled to {{date}}.',
    workspaceTitle: 'Reconcile {{name}}',
    workspaceSubtitle: 'Statement ending date {{date}}',
    editInfo: 'Edit info',
    saveForLater: 'Save for later',
    mustBeZero: 'The difference must be zero to finish',
    finishing: 'Finishing…',
    finish: 'Finish now',
    statementEndingBalance: 'Statement ending balance',
    clearedBalance: 'Cleared balance',
    difference: 'Difference',
    payments_one: '{{count}} payment',
    payments_other: '{{count}} payments',
    deposits_one: '{{count}} deposit',
    deposits_other: '{{count}} deposits',
    tabsLabel: 'Reconcile tabs',
    tabPayments: 'Payments',
    tabDeposits: 'Deposits',
    tabAll: 'All',
    noLines: 'No transactions to the statement ending date',
    colDate: 'Date',
    colClearedDate: 'Cleared date',
    colType: 'Type',
    colRef: 'Ref no.',
    colPayee: 'Payee',
    colMemo: 'Memo',
    colPayment: 'Payment ({{currency}})',
    colDeposit: 'Deposit ({{currency}})',
    tickAll: 'Tick all shown',
    clearedLine: 'Cleared: {{type}} {{ref}}',
    editTitle: 'Edit statement info',
    editBody: 'Correct the ending date or balance. Ticked transactions are kept.',
    endingBalance: 'Ending balance',
    endingDate: 'Ending date',
    negative: 'The ending balance is negative',
  },
  reconcileReport: {
    loadFailed: 'Could not run the report',
    back: 'Back to Reconcile',
    print: 'Print',
    periodEnding: '{{code}} {{name}}, period ending {{date}}',
    reconciledOn: 'Reconciled on {{date}}',
    reconciledOnBy: 'Reconciled on {{date}} by {{name}}',
    undoneOn: 'Undone on {{date}}: {{reason}}',
    inProgress: 'In progress',
    summary: 'Summary',
    transactions_one: '{{count}} transaction',
    transactions_other: '{{count}} transactions',
    beginningBalance: 'Statement beginning balance',
    clearedPaymentsCount: 'Cleared transactions – payments ({{count}})',
    clearedDepositsCount: 'Cleared transactions – deposits ({{count}})',
    endingBalance: 'Statement ending balance',
    unclearedPaymentsCount: 'Uncleared payments as of {{date}} ({{count}})',
    unclearedDepositsCount: 'Uncleared deposits as of {{date}} ({{count}})',
    registerBalanceAsOf: 'Register balance as of {{date}}',
    newAfterCount: 'New transactions after {{date}} ({{count}})',
    registerBalanceToday: 'Register balance today',
    clearedPayments: 'Cleared payments',
    clearedDeposits: 'Cleared deposits',
    unclearedPayments: 'Uncleared payments as of {{date}}',
    unclearedDeposits: 'Uncleared deposits as of {{date}}',
    newAfter: 'New transactions after {{date}}',
    footer: '{{org}} · Reconciliation report · Generated {{when}}',
    colDate: 'Date',
    colType: 'Type',
    colRef: 'Ref no.',
    colPayee: 'Payee',
    colMemo: 'Memo',
    colAmount: 'Amount',
  },
  transfer: {
    staffOnly: 'Only accounting staff can record transfers',
    loadFailed: 'Could not load bank accounts',
    leaveConfirm: 'You have unsaved changes. Leave without saving?',
    savedToast: 'Transfer saved as journal entry no. {{no}}.',
    view: 'View',
    title: 'Transfer',
    from: 'Transfer funds from *',
    to: 'Transfer funds to *',
    chooseAccount: 'Choose an account',
    amount: 'Transfer amount *',
    amountIn: 'Transfer amount ({{currency}}) *',
    received: 'Amount received ({{currency}}) *',
    receivedHint: 'The exchange rate is taken from the two amounts',
    date: 'Date *',
    memo: 'Memo',
    explainer:
      'Posts a journal entry: debit the account the money goes to, credit the account it leaves. When the bank statements come in, both statement lines match this transfer.',
  },
};

export const ar = {
  import: {
    title: 'استيراد كشف حساب بنكي',
    description:
      'حمّل ملف CSV أو OFX من البنك. تنتقل المعاملات إلى المعاملات البنكية › قيد المراجعة.',
    back: 'العودة إلى المعاملات البنكية',
    staffOnly: 'استيراد الكشوف متاح لموظفي المحاسبة فقط',
    staffOnlyHint: 'أرسل الكشف إلى محاسبك.',
    loadFailed: 'تعذّر تحميل الحسابات البنكية',
    noBanks: 'لا توجد حسابات بنكية بعد',
    noBanksHint: 'أضف أولاً حساباً من نوع بنك أو بطاقة ائتمان إلى دليل الحسابات.',
    steps: {
      upload: 'الحساب والملف',
      map: 'مطابقة الأعمدة',
      review: 'المراجعة',
      done: 'تم',
    },
    stepsLabel: 'خطوات الاستيراد',
    fields: {
      date: 'التاريخ',
      description: 'الوصف',
      payee: 'المستفيد',
      reference: 'المرجع',
      amount: 'المبلغ',
      moneyIn: 'المبالغ الواردة',
      moneyOut: 'المبالغ الصادرة',
      balance: 'الرصيد',
    },
    listSeparator: '، ',
    wrongFile: 'اختر ملف ‎.csv أو ‎.ofx. في Excel، استخدم ملف › حفظ باسم › CSV UTF-8.',
    noRows: 'يحتوي الملف على صف العناوين فقط دون أي معاملات.',
    tooManyRows: 'يحتوي الملف على {{rows}} صف؛ استورد {{max}} صف كحد أقصى في كل مرة.',
    bankAccount: 'الحساب البنكي',
    dropTitle: 'اسحب ملف الكشف وأفلته هنا، أو استعرض',
    dropHint:
      'CSV (من أي بنك: تطابق الأعمدة مرة واحدة ونتذكرها لهذا الحساب) أو OFX / QFX. حتى {{max}} معاملة في الملف الواحد. تُتجاوز المعاملات المستوردة سابقاً.',
    reading: 'جارٍ القراءة…',
    browse: 'استعراض',
    fileLabel: 'ملف الكشف',
    hintDescription: 'البيان أو التفاصيل. يُستخدم المستفيد عند تركه فارغاً.',
    hintReference: 'رقم الشيك أو مرجع المعاملة.',
    hintMoneyIn: 'الإيداعات / المبالغ الدائنة.',
    hintMoneyOut: 'السحوبات / المبالغ المدينة.',
    hintBalance: 'الرصيد الجاري؛ يصبح آخر رصيد هو رصيد البنك.',
    mapMissing: 'اربط عموداً بـ {{fields}}.',
    mapSplitMissing: 'اربط عمود المبالغ الواردة أو المبالغ الصادرة.',
    mapTwice: 'كل عمود يغذّي حقلاً واحداً فقط: العمود {{columns}} مربوط مرتين.',
    mapIntroSaved_zero:
      'لا صفوف إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroSaved_one:
      'صف واحد إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroSaved_two:
      'صفان إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroSaved_few:
      '{{n}} صفوف إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroSaved_many:
      '{{n}} صفاً إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroSaved_other:
      '{{n}} صف إلى <b>{{bank}}</b>. استخدمنا المطابقة المحفوظة لهذا الحساب؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_zero:
      'لا صفوف إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_one:
      'صف واحد إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_two:
      'صفان إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_few:
      '{{n}} صفوف إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_many:
      '{{n}} صفاً إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    mapIntroAuto_other:
      '{{n}} صف إلى <b>{{bank}}</b>. طابقنا ما تعرّفنا عليه؛ راجع الباقي. تُحفظ المطابقة لهذا الحساب.',
    dateFormat: 'صيغة التاريخ',
    amountsIn: 'المبالغ موجودة في',
    oneColumn: 'عمود واحد (موجب وسالب)',
    twoColumns: 'عمودان (وارد وصادر)',
    positiveAre: 'المبالغ الموجبة هي',
    positiveIn: 'مبالغ واردة (معظم الحسابات البنكية)',
    positiveOut: 'مبالغ صادرة (معظم بطاقات الائتمان)',
    colField: 'حقل Outss Accounting',
    colColumn: 'العمود في ملفك',
    colFirstRow: 'الصف الأول',
    columnFor: 'العمود الخاص بـ {{field}}',
    notMapped: 'غير مربوط',
    back2: 'رجوع',
    checking: 'جارٍ التحقق من المعاملات…',
    next: 'التالي',
    imported_zero: 'لم تُستورد أي معاملة إلى {{bank}}.',
    imported_one: 'استُوردت معاملة واحدة إلى {{bank}}.',
    imported_two: 'استُوردت معاملتان إلى {{bank}}.',
    imported_few: 'استُوردت {{n}} معاملات إلى {{bank}}.',
    imported_many: 'استُوردت {{n}} معاملةً إلى {{bank}}.',
    imported_other: 'استُوردت {{n}} معاملة إلى {{bank}}.',
    autoAdded_zero: 'لم تُضف القواعد أي معاملة تلقائياً.',
    autoAdded_one: 'أضافت القواعد معاملة واحدة تلقائياً.',
    autoAdded_two: 'أضافت القواعد معاملتين تلقائياً.',
    autoAdded_few: 'أضافت القواعد {{n}} معاملات تلقائياً.',
    autoAdded_many: 'أضافت القواعد {{n}} معاملةً تلقائياً.',
    autoAdded_other: 'أضافت القواعد {{n}} معاملة تلقائياً.',
    ruleSuggested_zero: 'لا توجد معاملات عليها اقتراح من قاعدة.',
    ruleSuggested_one: 'معاملة واحدة عليها اقتراح من قاعدة.',
    ruleSuggested_two: 'معاملتان عليهما اقتراح من قاعدة.',
    ruleSuggested_few: '{{n}} معاملات عليها اقتراح من قاعدة.',
    ruleSuggested_many: '{{n}} معاملةً عليها اقتراح من قاعدة.',
    ruleSuggested_other: '{{n}} معاملة عليها اقتراح من قاعدة.',
    duplicatesSkipped_zero: 'لم تُتجاوز أي معاملة مكررة.',
    duplicatesSkipped_one: 'تُجووزت معاملة مكررة واحدة.',
    duplicatesSkipped_two: 'تُجووزت معاملتان مكررتان.',
    duplicatesSkipped_few: 'تُجووزت {{n}} معاملات مكررة.',
    duplicatesSkipped_many: 'تُجووزت {{n}} معاملةً مكررة.',
    duplicatesSkipped_other: 'تُجووزت {{n}} معاملة مكررة.',
    errorsSkipped_zero: 'لم يُتجاوز أي صف بسبب أخطاء.',
    errorsSkipped_one: 'تُجووز صف واحد فيه أخطاء.',
    errorsSkipped_two: 'تُجووز صفان فيهما أخطاء.',
    errorsSkipped_few: 'تُجووزت {{n}} صفوف فيها أخطاء.',
    errorsSkipped_many: 'تُجووز {{n}} صفاً فيها أخطاء.',
    errorsSkipped_other: 'تُجووز {{n}} صف فيها أخطاء.',
    ready_zero: '{{ok}} من أصل {{n}} معاملة جاهزة للاستيراد.',
    ready_one: '{{ok}} من أصل معاملة واحدة جاهزة للاستيراد.',
    ready_two: '{{ok}} من أصل معاملتين جاهزة للاستيراد.',
    ready_few: '{{ok}} من أصل {{n}} معاملات جاهزة للاستيراد.',
    ready_many: '{{ok}} من أصل {{n}} معاملةً جاهزة للاستيراد.',
    ready_other: '{{ok}} من أصل {{n}} معاملة جاهزة للاستيراد.',
    duplicatesWillSkip_zero: 'لا توجد معاملات موجودة مسبقاً في Outss Accounting.',
    duplicatesWillSkip_one: 'معاملة واحدة موجودة مسبقاً في Outss Accounting وستُتجاوز.',
    duplicatesWillSkip_two: 'معاملتان موجودتان مسبقاً في Outss Accounting وستُتجاوزان.',
    duplicatesWillSkip_few: '{{n}} معاملات موجودة مسبقاً في Outss Accounting وستُتجاوز.',
    duplicatesWillSkip_many: '{{n}} معاملةً موجودة مسبقاً في Outss Accounting وستُتجاوز.',
    duplicatesWillSkip_other: '{{n}} معاملة موجودة مسبقاً في Outss Accounting وستُتجاوز.',
    errorsWillSkip_zero: 'لا توجد صفوف فيها أخطاء.',
    errorsWillSkip_one: 'صف واحد فيه أخطاء وسيُتجاوز.',
    errorsWillSkip_two: 'صفان فيهما أخطاء وسيُتجاوزان.',
    errorsWillSkip_few: '{{n}} صفوف فيها أخطاء وستُتجاوز.',
    errorsWillSkip_many: '{{n}} صفاً فيها أخطاء وستُتجاوز.',
    errorsWillSkip_other: '{{n}} صف فيها أخطاء وستُتجاوز.',
    statementBalance: 'رصيد الكشف {{amount}} في {{date}}.',
    onlyProblems: 'عرض المكررات والأخطاء فقط',
    colRow: 'الصف',
    colDate: 'التاريخ',
    colDescription: 'الوصف',
    colAmount: 'المبلغ',
    colStatus: 'الحالة',
    colErrors: 'الأخطاء',
    statusImported: 'مستوردة',
    statusReady: 'جاهزة',
    statusDuplicate: 'مكررة',
    statusError: 'خطأ',
    transaction: 'المعاملة',
    errorField: '{{field}}: ',
    another: 'استيراد ملف آخر',
    reviewTransactions: 'مراجعة المعاملات',
    importing: 'جارٍ الاستيراد…',
    importN_zero: 'استيراد',
    importN_one: 'استيراد معاملة واحدة',
    importN_two: 'استيراد معاملتين',
    importN_few: 'استيراد {{n}} معاملات',
    importN_many: 'استيراد {{n}} معاملةً',
    importN_other: 'استيراد {{n}} معاملة',
  },
  reconcile: {
    title: 'التسوية البنكية',
    description:
      'قارن الحساب بكشف البنك، وحدّد ما تمت تسويته، ثم أنهِ التسوية عندما يصبح الفرق صفراً.',
    loadFailed: 'تعذّر تحميل الحسابات البنكية',
    noBanks: 'لا توجد حسابات بنكية لتسويتها',
    statusInProgress: 'قيد التنفيذ',
    statusReconciled: 'تمت التسوية',
    statusUndone: 'تم التراجع عنها',
    deleted: 'حُذفت التسوية. لم يتغير شيء في الدفاتر.',
    whichAccount: 'أي حساب تريد تسويته؟',
    account: 'الحساب',
    lastEnding: 'تاريخ انتهاء آخر كشف {{date}}',
    neverReconciled: 'لم تتم تسويته من قبل',
    savedForLater: 'حُفظت تسوية حتى {{date}} (الرصيد الختامي {{amount}}) لاستكمالها لاحقاً.',
    resumeReconciling: 'استكمال التسوية',
    addInfo: 'أدخل المعلومات التالية',
    beginningBalance: 'الرصيد الافتتاحي',
    beginningHint: 'من آخر تسوية.',
    endingBalanceRequired: 'الرصيد الختامي *',
    endingCardHint: 'المبلغ المستحق في الكشف.',
    endingDateRequired: 'تاريخ الانتهاء *',
    negativeCard: 'الرصيد الختامي سالب (للبطاقة رصيد دائن)',
    negativeBank: 'الرصيد الختامي سالب (سحب على المكشوف)',
    starting: 'جارٍ البدء…',
    start: 'بدء التسوية',
    historyFor: 'سجل التسويات لحساب {{name}}',
    noHistory: 'لا توجد تسويات بعد',
    colEndingDate: 'تاريخ انتهاء الكشف',
    colStatus: 'الحالة',
    colEndingBalance: 'الرصيد الختامي',
    colBeginningBalance: 'الرصيد الافتتاحي',
    colReconciledOn: 'تاريخ التسوية',
    colAction: 'الإجراء',
    resume: 'استكمال',
    delete: 'حذف',
    viewReport: 'عرض التقرير',
    undo: 'تراجع',
    deleteTitle: 'حذف التسوية الجارية؟',
    deleteBody: 'ستُحذف التحديدات المحفوظة. لن يتغير شيء في الدفاتر.',
    undoneToast: 'تم التراجع عن التسوية. لم تعد معاملاتها مُسوّاة.',
    undoTitle: 'التراجع عن التسوية حتى {{date}}؟',
    undoBody:
      'تعود كل معاملة سوّتها إلى غير مُسوّاة، ويمكن تعديلها أو عكسها مجدداً. تبقى التسوية في السجل بحالة "تم التراجع عنها". لا يُرحَّل أي قيد.',
    reason: 'السبب',
    reasonHint: 'يُسجَّل في سجل التدقيق.',
    undoing: 'جارٍ التراجع…',
    undoReconciliation: 'التراجع عن التسوية',
    loadReconciliationFailed: 'تعذّر تحميل التسوية',
    leaveConfirm: 'هل تريد المغادرة دون حفظ تحديداتك؟ استخدم "حفظ لوقت لاحق" للاحتفاظ بها.',
    savedToast: 'حُفظت لوقت لاحق. استكملها من صفحة التسوية البنكية.',
    finishedToast: 'تمت تسوية {{name}} حتى {{date}}.',
    workspaceTitle: 'تسوية {{name}}',
    workspaceSubtitle: 'تاريخ انتهاء الكشف {{date}}',
    editInfo: 'تعديل المعلومات',
    saveForLater: 'حفظ لوقت لاحق',
    mustBeZero: 'يجب أن يكون الفرق صفراً لإنهاء التسوية',
    finishing: 'جارٍ الإنهاء…',
    finish: 'إنهاء الآن',
    statementEndingBalance: 'الرصيد الختامي في الكشف',
    clearedBalance: 'الرصيد المُسوّى',
    difference: 'الفرق',
    payments_zero: 'لا مدفوعات',
    payments_one: 'دفعة واحدة',
    payments_two: 'دفعتان',
    payments_few: '{{count}} دفعات',
    payments_many: '{{count}} دفعةً',
    payments_other: '{{count}} دفعة',
    deposits_zero: 'لا إيداعات',
    deposits_one: 'إيداع واحد',
    deposits_two: 'إيداعان',
    deposits_few: '{{count}} إيداعات',
    deposits_many: '{{count}} إيداعاً',
    deposits_other: '{{count}} إيداع',
    tabsLabel: 'تبويبات التسوية',
    tabPayments: 'المدفوعات',
    tabDeposits: 'الإيداعات',
    tabAll: 'الكل',
    noLines: 'لا توجد معاملات حتى تاريخ انتهاء الكشف',
    colDate: 'التاريخ',
    colClearedDate: 'تاريخ التسوية',
    colType: 'النوع',
    colRef: 'رقم المرجع',
    colPayee: 'المستفيد',
    colMemo: 'المذكرة',
    colPayment: 'الدفعة ({{currency}})',
    colDeposit: 'الإيداع ({{currency}})',
    tickAll: 'تحديد كل المعروض',
    clearedLine: 'مُسوّاة: {{type}} {{ref}}',
    editTitle: 'تعديل معلومات الكشف',
    editBody: 'صحّح تاريخ الانتهاء أو الرصيد. تبقى المعاملات المحددة كما هي.',
    endingBalance: 'الرصيد الختامي',
    endingDate: 'تاريخ الانتهاء',
    negative: 'الرصيد الختامي سالب',
  },
  reconcileReport: {
    loadFailed: 'تعذّر تشغيل التقرير',
    back: 'العودة إلى التسوية البنكية',
    print: 'طباعة',
    periodEnding: '{{code}} {{name}}، الفترة المنتهية في {{date}}',
    reconciledOn: 'تمت التسوية في {{date}}',
    reconciledOnBy: 'تمت التسوية في {{date}} بواسطة {{name}}',
    undoneOn: 'تم التراجع في {{date}}: {{reason}}',
    inProgress: 'قيد التنفيذ',
    summary: 'الملخص',
    transactions_zero: 'لا معاملات',
    transactions_one: 'معاملة واحدة',
    transactions_two: 'معاملتان',
    transactions_few: '{{count}} معاملات',
    transactions_many: '{{count}} معاملةً',
    transactions_other: '{{count}} معاملة',
    beginningBalance: 'الرصيد الافتتاحي في الكشف',
    clearedPaymentsCount: 'المعاملات المُسوّاة – المدفوعات ({{count}})',
    clearedDepositsCount: 'المعاملات المُسوّاة – الإيداعات ({{count}})',
    endingBalance: 'الرصيد الختامي في الكشف',
    unclearedPaymentsCount: 'المدفوعات غير المُسوّاة حتى {{date}} ({{count}})',
    unclearedDepositsCount: 'الإيداعات غير المُسوّاة حتى {{date}} ({{count}})',
    registerBalanceAsOf: 'رصيد السجل حتى {{date}}',
    newAfterCount: 'المعاملات الجديدة بعد {{date}} ({{count}})',
    registerBalanceToday: 'رصيد السجل اليوم',
    clearedPayments: 'المدفوعات المُسوّاة',
    clearedDeposits: 'الإيداعات المُسوّاة',
    unclearedPayments: 'المدفوعات غير المُسوّاة حتى {{date}}',
    unclearedDeposits: 'الإيداعات غير المُسوّاة حتى {{date}}',
    newAfter: 'المعاملات الجديدة بعد {{date}}',
    footer: '{{org}} · تقرير التسوية البنكية · أُنشئ في {{when}}',
    colDate: 'التاريخ',
    colType: 'النوع',
    colRef: 'رقم المرجع',
    colPayee: 'المستفيد',
    colMemo: 'المذكرة',
    colAmount: 'المبلغ',
  },
  transfer: {
    staffOnly: 'تسجيل التحويلات متاح لموظفي المحاسبة فقط',
    loadFailed: 'تعذّر تحميل الحسابات البنكية',
    leaveConfirm: 'لديك تغييرات غير محفوظة. هل تريد المغادرة دون حفظ؟',
    savedToast: 'حُفظ التحويل كقيد يومية رقم {{no}}.',
    view: 'عرض',
    title: 'تحويل',
    from: 'تحويل الأموال من *',
    to: 'تحويل الأموال إلى *',
    chooseAccount: 'اختر حساباً',
    amount: 'مبلغ التحويل *',
    amountIn: 'مبلغ التحويل ({{currency}}) *',
    received: 'المبلغ المستلم ({{currency}}) *',
    receivedHint: 'يُحتسب سعر الصرف من المبلغين',
    date: 'التاريخ *',
    memo: 'المذكرة',
    explainer:
      'يُرحّل قيد يومية: مدين للحساب الذي تذهب إليه الأموال، ودائن للحساب الذي تخرج منه. عند وصول كشوف البنك، يُطابَق سطرا الكشفين مع هذا التحويل.',
  },
};
